import { providers, Contract } from 'ethers';
import {
  AaveV3Ethereum,
  AaveV3Arbitrum,
  AaveV3Avalanche,
  AaveV3Sonic,
  AaveV3Base,
  AaveV3BNB,
  AaveV3ZkSync,
  AaveV3Linea,
  AaveV3Optimism,
} from '@bgd-labs/aave-address-book';
import { UiPoolDataProvider } from '@aave/contract-helpers/dist/esm/v3-UiPoolDataProvider-contract';
import { formatReserves } from '@aave/math-utils/dist/esm/formatters/reserve';
import { formatUserSummary } from '@aave/math-utils/dist/esm/formatters/user';

// Minimal ABI for IPool.getUserAccountData
const IPoolAbi = [
  {
    constant: true,
    inputs: [{ internalType: 'address', name: 'user', type: 'address' }],
    name: 'getUserAccountData',
    outputs: [
      { internalType: 'uint256', name: 'totalCollateralBase', type: 'uint256' },
      { internalType: 'uint256', name: 'totalDebtBase', type: 'uint256' },
      { internalType: 'uint256', name: 'availableBorrowsBase', type: 'uint256' },
      { internalType: 'uint256', name: 'currentLiquidationThreshold', type: 'uint256' },
      { internalType: 'uint256', name: 'ltv', type: 'uint256' },
      { internalType: 'uint256', name: 'healthFactor', type: 'uint256' },
    ],
    payable: false,
    stateMutability: 'view',
    type: 'function',
  },
];

export const NETWORKS = {
  ethereum: {
    label: 'Ethereum',
    rpcUrls: [
      'https://ethereum.publicnode.com',
      'https://rpc.ankr.com/eth',
      'https://cloudflare-eth.com',
    ],
    addresses: AaveV3Ethereum,
    env: 'REACT_APP_RPC_ETHEREUM',
  },
  arbitrum: {
    label: 'Arbitrum',
    rpcUrls: ['https://arb1.arbitrum.io/rpc', 'https://rpc.ankr.com/arbitrum'],
    addresses: AaveV3Arbitrum,
    env: 'REACT_APP_RPC_ARBITRUM',
  },
  avalanche: {
    label: 'Avalanche',
    rpcUrls: ['https://api.avax.network/ext/bc/C/rpc', 'https://rpc.ankr.com/avalanche'],
    addresses: AaveV3Avalanche,
    env: 'REACT_APP_RPC_AVALANCHE',
  },
  base: {
    label: 'Base',
    rpcUrls: ['https://mainnet.base.org', 'https://rpc.ankr.com/base'],
    addresses: AaveV3Base,
    env: 'REACT_APP_RPC_BASE',
  },
  bnb: {
    label: 'BNB Chain',
    rpcUrls: ['https://bsc-dataseed.binance.org', 'https://rpc.ankr.com/bsc'],
    addresses: AaveV3BNB,
    env: 'REACT_APP_RPC_BNB',
  },
  zksync: {
    label: 'zkSync Era',
    rpcUrls: ['https://mainnet.era.zksync.io'],
    addresses: AaveV3ZkSync,
    env: 'REACT_APP_RPC_ZKSYNC',
  },
  linea: {
    label: 'Linea',
    rpcUrls: ['https://rpc.linea.build', 'https://1rpc.io/linea'],
    addresses: AaveV3Linea,
    env: 'REACT_APP_RPC_LINEA',
  },
  optimism: {
    label: 'Optimism',
    rpcUrls: ['https://mainnet.optimism.io', 'https://rpc.ankr.com/optimism'],
    addresses: AaveV3Optimism,
    env: 'REACT_APP_RPC_OPTIMISM',
  },
  sonic: {
    label: 'Sonic',
    rpcUrls: ['https://rpc.soniclabs.com'],
    addresses: AaveV3Sonic,
    env: 'REACT_APP_RPC_SONIC',
  },
};

function toNumberString(value) {
  try {
    return value?.toString?.() ?? '0';
  } catch (_e) {
    return '0';
  }
}

// Caches to avoid repeated provider instantiation and heavy pool/reserve calls
const providerCache = new Map(); // key: `${rpc}|${chainId}` -> StaticJsonRpcProvider
const uiPoolCache = new Map(); // key: `${rpc}|${chainId}` -> UiPoolDataProvider
const reservesCache = new Map(); // key: `${chainId}` -> { ts, data }
const RESERVES_TTL_MS = 60_000; // 60s

function getProvider(rpc, chainId) {
  const key = `${rpc}|${chainId}`;
  const cached = providerCache.get(key);
  if (cached) return cached;
  // Use StaticJsonRpcProvider to avoid network detection chatter
  const p = new providers.StaticJsonRpcProvider(rpc, chainId);
  providerCache.set(key, p);
  return p;
}

function getUiPool(rpc, chainId, uiPoolAddress) {
  const key = `${rpc}|${chainId}`;
  const cached = uiPoolCache.get(key);
  if (cached) return cached;
  const provider = getProvider(rpc, chainId);
  const uiPool = new UiPoolDataProvider({ uiPoolDataProviderAddress: uiPoolAddress, provider, chainId });
  uiPoolCache.set(key, uiPool);
  return uiPool;
}

export async function fetchAaveUserData(walletAddress) {
  const results = [];
  const nowTs = Math.floor(Date.now() / 1000);

  for (const [key, config] of Object.entries(NETWORKS)) {
    const { addresses, label } = config;
    const tryFetch = async (rpc) => {
      const chainId = addresses.CHAIN_ID;
      const provider = getProvider(rpc, chainId);
      const uiPool = getUiPool(rpc, chainId, addresses.UI_POOL_DATA_PROVIDER);

      // Reserves data (cache for TTL)
      let reservesHumanized;
      const cached = reservesCache.get(chainId);
      if (cached && (Date.now() - cached.ts) < RESERVES_TTL_MS) {
        reservesHumanized = cached.data;
      } else {
        reservesHumanized = await uiPool.getReservesHumanized({
          lendingPoolAddressProvider: addresses.POOL_ADDRESSES_PROVIDER,
        });
        reservesCache.set(chainId, { ts: Date.now(), data: reservesHumanized });
      }

      // User-specific reserves (no cache)
      const userReservesHumanized = await uiPool.getUserReservesHumanized({
        lendingPoolAddressProvider: addresses.POOL_ADDRESSES_PROVIDER,
        user: walletAddress,
      });

      const formattedReserves = formatReserves({
        reserves: reservesHumanized.reservesData,
        currentTimestamp: nowTs,
        marketReferencePriceInUsd:
          reservesHumanized.baseCurrencyData.marketReferenceCurrencyPriceInUsd,
        marketReferenceCurrencyDecimals:
          reservesHumanized.baseCurrencyData.marketReferenceCurrencyDecimals,
      });

      const summary = formatUserSummary({
        userReserves: userReservesHumanized.userReserves,
        formattedReserves,
        marketReferencePriceInUsd:
          reservesHumanized.baseCurrencyData.marketReferenceCurrencyPriceInUsd,
        marketReferenceCurrencyDecimals:
          reservesHumanized.baseCurrencyData.marketReferenceCurrencyDecimals,
        currentTimestamp: nowTs,
        userEmodeCategoryId: userReservesHumanized.userEmodeCategoryId,
      });

      const poolContract = new Contract(addresses.POOL, IPoolAbi, provider);
      const accountData = await poolContract.getUserAccountData(walletAddress);

      return {
        networkKey: key,
        networkLabel: `${label} · Aave`,
        chainId,
        poolSummary: {
          totalCollateralBase: toNumberString(accountData.totalCollateralBase),
          totalDebtBase: toNumberString(accountData.totalDebtBase),
          availableBorrowsBase: toNumberString(accountData.availableBorrowsBase),
          currentLiquidationThreshold: toNumberString(
            accountData.currentLiquidationThreshold
          ),
          ltv: toNumberString(accountData.ltv),
          healthFactor: toNumberString(accountData.healthFactor),
        },
        userSummary: summary,
      };
    };

    try {
      // Build RPC list with env override (comma-separated supported)
      const envOverride = config.env ? process.env[config.env] : undefined;
      const rpcListFromEnv = envOverride
        ? envOverride.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
      const rpcCandidates = rpcListFromEnv.length > 0 ? rpcListFromEnv : (config.rpcUrls || []);

      let lastError = null;
      for (const rpc of rpcCandidates) {
        try {
          const res = await tryFetch(rpc);
          results.push(res);
          lastError = null;
          break;
        } catch (err) {
          lastError = err;
        }
      }
      if (lastError) {
        results.push({ networkKey: key, networkLabel: label, error: lastError?.message || 'Failed to load data' });
      }
    } catch (error) {
      results.push({
        networkKey: key,
        networkLabel: label,
        error: error?.message || 'Failed to load data',
      });
    }
  }

  return results;
}

export function getSupportedNetworkLabels() {
  return Object.values(NETWORKS).map((n) => n.label);
}


